import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { apiFetch } from "../../../utils/apiFetch";
import { mapContactRow } from "../../../utils/contactRowMapper";
import { buildRecipients, type KontaktAktionMode, type Recipient } from "./empfaenger";
import "./vorgangDetail.css";
import { hinweisen } from "../../redesign/bestaetigen";
import { alleDokumente, alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * SP8 — „Mail verfassen" + „Anruf" als zwei Segmente der durchgängigen
 * Aktionsleiste, jeweils mit AP-Empfängerwahl (Review B20).
 *
 * Beim ersten Klick wird das Kontakt-Detail einmal nachgeladen
 * (`include=relations,relations-deep`, KomCell-Muster) und gecacht. Danach:
 *   • keine Adresse/Nummer  → verständliche Meldung,
 *   • genau eine (z. B. Person ohne AP) → direkter Flow (Compose/Call),
 *   • mehrere (Firma mit APs) → Empfänger-Popover „Zentrale | AP1 | AP2 …".
 *
 * Die konkrete Aktion liefert die Seite (`onCompose`/`onCall`); dieses Segment
 * kümmert sich nur um Laden + Auswahl.
 *
 * Mit `nur` wird eine einzelne Aktion gerendert — der Anrufe-Reiter des Vorgangs
 * nutzt das für seinen „Anruf starten"-Knopf (PO 02.08.2026: beim Anruf soll man
 * Zentrale ODER Ansprechpartner wählen können).
 */

interface Props {
    kontaktId: number | null;
    kontaktName: string | null;
    disabled?: boolean;
    onCompose: (email: string) => void;
    onCall: (phone: string, name?: string) => void;
    /** Nur EIN Segment rendern (z. B. „Anruf" im Anrufe-Reiter). */
    nur?: KontaktAktionMode;
    /** Beschriftung überschreiben (Default „Mail verfassen" / „Anruf"). */
    label?: string;
    /** Optische Variante: `seg` = Segment der Aktionsleiste, `btn` = Einzelknopf. */
    variant?: "seg" | "btn";
    /**
     * Eigene Knopfklasse statt der `vgd-*`-Variante.
     *
     * Gebraucht von der chronologischen Ansicht: deren Baum hat keinen
     * `.vgd`-Vorfahren, und `.vgd-btn` liest `var(--vgd-aub-100)` /
     * `var(--vgd-aub)` — beide sind ausschließlich auf `.vgd` definiert. Die
     * `var()` sind dort ungültig, der Rand fällt auf `unset`: der Anruf-Knopf
     * steht in v3 heute ganz ohne Rand neben vier gerahmten Geschwistern.
     */
    className?: string;
    /** Icon vor der Beschriftung — die Knopfreihe in v3 ist durchgehend bebildert. */
    icon?: React.ReactNode;
    /** Vorlesetext und Tooltip — für Knöpfe ohne sichtbare Beschriftung (Handy-Kopf). */
    ariaLabel?: string;
}

interface PopState {
    mode: KontaktAktionMode;
    recips: Recipient[];
    pos: { top: number; left: number; width: number };
}

const anchorPos = (el: HTMLElement | null): PopState["pos"] => {
    const r = el?.getBoundingClientRect();
    return r
        ? { top: r.bottom + 4, left: r.left, width: Math.max(r.width, 240) }
        : { top: 80, left: 40, width: 240 };
};

export const KontaktAktionen: React.FC<Props> = ({
    kontaktId,
    kontaktName,
    disabled,
    onCompose,
    onCall,
    nur,
    label,
    variant = "seg",
    className,
    icon,
    ariaLabel,
}) => {
    const rowRef = useRef<any>(null);
    const [busy, setBusy] = useState<KontaktAktionMode | null>(null);
    const [pop, setPop] = useState<PopState | null>(null);
    const mailRef = useRef<HTMLButtonElement | null>(null);
    const anrufRef = useRef<HTMLButtonElement | null>(null);
    const dropRef = useRef<HTMLDivElement | null>(null);

    // Kontaktwechsel → Cache verwerfen, offenes Popover schließen.
    useEffect(() => {
        rowRef.current = null;
        setPop(null);
    }, [kontaktId]);

    const ensureRow = useCallback(async () => {
        if (rowRef.current) return rowRef.current;
        if (kontaktId == null) return null;
        const { data } = await apiFetch<any>(`contact/${kontaktId}`, {
            params: { include: "relations,relations-deep" },
        });
        const row = mapContactRow(data);
        rowRef.current = row;
        return row;
    }, [kontaktId]);

    const perform = useCallback(
        (mode: KontaktAktionMode, r: Recipient) => {
            if (mode === "email") onCompose(r.value);
            else onCall(r.value, r.isZentrale ? kontaktName ?? undefined : r.name);
        },
        [onCompose, onCall, kontaktName],
    );

    const trigger = useCallback(
        async (mode: KontaktAktionMode, anchor: HTMLButtonElement | null) => {
            if (busy) return;
            setBusy(mode);
            try {
                const row = await ensureRow();
                if (!row) {
                    void hinweisen({ titel: "Kontaktdaten fehlen", text: "Die Kontaktdaten konnten nicht geladen werden.", ton: "danger" });
                    return;
                }
                const recips = buildRecipients(row, mode);
                if (recips.length === 0) {
                    void hinweisen({
                        titel: mode === "email" ? "Keine E-Mail-Adresse" : "Keine Telefonnummer",
                        text:
                            mode === "email"
                                ? "Für diesen Kontakt ist keine E-Mail-Adresse hinterlegt."
                                : "Für diesen Kontakt ist keine Telefonnummer hinterlegt.",
                    });
                    return;
                }
                if (recips.length === 1) {
                    perform(mode, recips[0]);
                    return;
                }
                setPop({ mode, recips, pos: anchorPos(anchor) });
            } catch (err) {
                console.error("[KontaktAktionen] Kontaktdaten laden fehlgeschlagen", err);
                void hinweisen({ titel: "Kontaktdaten fehlen", text: "Die Kontaktdaten konnten nicht geladen werden.", ton: "danger" });
            } finally {
                setBusy(null);
            }
        },
        [busy, ensureRow, perform],
    );

    // Popover: nachführen bei Scroll/Resize, Schließen bei Klick daneben / Escape.
    useEffect(() => {
        if (!pop) return;
        const anchorRef = pop.mode === "email" ? mailRef : anrufRef;
        const reposition = () =>
            setPop((p) => (p ? { ...p, pos: anchorPos(anchorRef.current) } : p));
        alleFenster.addEventListener("scroll", reposition, true);
        alleFenster.addEventListener("resize", reposition);
        const onDocClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (
                mailRef.current?.contains(t) ||
                anrufRef.current?.contains(t) ||
                dropRef.current?.contains(t)
            )
                return;
            setPop(null);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setPop(null);
        };
        alleDokumente.addEventListener("pointerdown", onDocClick);
        alleFenster.addEventListener("keydown", onKey);
        return () => {
            alleFenster.removeEventListener("scroll", reposition, true);
            alleFenster.removeEventListener("resize", reposition);
            alleDokumente.removeEventListener("pointerdown", onDocClick);
            alleFenster.removeEventListener("keydown", onKey);
        };
    }, [pop?.mode]);

    // `className` schlägt die Variante — ohne Angabe bleibt alles wie bisher.
    const btnClass = className ?? (variant === "btn" ? "vgd-btn" : "vgd-actionbar-seg");

    return (
        <>
            {nur !== "phone" && (
                <button
                    ref={mailRef}
                    type="button"
                    className={btnClass}
                    disabled={disabled || busy === "email"}
                    {...(ariaLabel ? { "aria-label": ariaLabel, title: ariaLabel } : {})}
                    onClick={() => void trigger("email", mailRef.current)}
                >
                    {icon}
                    {label ?? "Mail verfassen"}
                </button>
            )}
            {nur !== "email" && (
                <button
                    ref={anrufRef}
                    type="button"
                    className={btnClass}
                    disabled={disabled || busy === "phone"}
                    {...(ariaLabel ? { "aria-label": ariaLabel, title: ariaLabel } : {})}
                    onClick={() => void trigger("phone", anrufRef.current)}
                >
                    {icon}
                    {label ?? "Anruf"}
                </button>
            )}
            {pop &&
                createPortal(
                    <div
                        ref={dropRef}
                        className="vgd vgd-drop vgd-recip-drop"
                        style={{
                            position: "fixed",
                            top: pop.pos.top,
                            left: pop.pos.left,
                            width: pop.pos.width,
                            zIndex: 100000,
                        }}
                    >
                        <div className="vgd-drop-head">
                            {pop.mode === "email" ? "Mail an wen?" : "Anruf an wen?"}
                        </div>
                        {pop.recips.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                className="vgd-recip-opt"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setPop(null);
                                    perform(pop.mode, r);
                                }}
                            >
                                <span className="vgd-recip-name">{r.name}</span>
                                <span className="vgd-recip-val">
                                    {r.value}
                                    {r.sub ? ` · ${r.sub}` : ""}
                                </span>
                            </button>
                        ))}
                    </div>,
                    document.body,
                )}
        </>
    );
};
